import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TimerPanel } from './TimerPanel.tsx';

// Document Picture-in-Picture gives a genuine always-on-top OS window that
// floats over Teams / RDP / anything else the tech is working in. Chrome and
// Edge 116+; everything else falls back to a plain popup on the /timer route.
interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
  window: Window | null;
}

function pipApi(): DocumentPictureInPicture | null {
  const api = (window as unknown as { documentPictureInPicture?: DocumentPictureInPicture })
    .documentPictureInPicture;
  return api ?? null;
}

export function isPipSupported(): boolean {
  return pipApi() !== null;
}

/**
 * Copy the host page's styles into the PiP document.
 *
 * Both paths are needed: Vite injects Tailwind as inline <style> in dev and
 * emits a <link> in prod. `cssRules` throws on cross-origin sheets, so the
 * link clone is also the fallback for those.
 */
function copyStyles(target: Window): void {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join('\n');
      const style = target.document.createElement('style');
      style.textContent = rules;
      target.document.head.appendChild(style);
    } catch {
      const owner = sheet.ownerNode as HTMLLinkElement | null;
      if (owner?.href) {
        const link = target.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = owner.href;
        target.document.head.appendChild(link);
      }
    }
  }
  // The design-system custom properties hang off :root / <html>, so carry the
  // class and inline styles over or every var() resolves to nothing.
  target.document.documentElement.className = document.documentElement.className;
  target.document.body.className = 'bg-white';
}

export function useMiniTimerWindow(): {
  open: () => Promise<void>;
  close: () => void;
  isOpen: boolean;
  supported: boolean;
  portal: React.ReactNode;
} {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  // Mirrors pipWindow so the unmount cleanup can close it without taking the
  // window as a dependency (which would tear it down on every state change).
  const pipRef = useRef<Window | null>(null);

  const close = useCallback(() => {
    pipRef.current?.close();
    pipRef.current = null;
    setPipWindow(null);
  }, []);

  const open = useCallback(async () => {
    // A popup on the /timer route is the next best thing to PiP, and it's
    // pinnable via the browser's "open as window" for a permanent dock.
    const openPopup = () => window.open('/timer', 't3timer', 'popup,width=360,height=360');

    const api = pipApi();
    if (!api) {
      openPopup();
      return;
    }
    if (api.window) {
      api.window.focus();
      return;
    }
    try {
      // Tall enough for Customer / Ticket / Time type stacked; the running
      // state is much shorter and the tech can resize either way.
      const w = await api.requestWindow({ width: 340, height: 340 });
      copyStyles(w);
      w.document.title = 'T3 Timer';
      w.addEventListener('pagehide', () => {
        pipRef.current = null;
        setPipWindow(null);
      });
      pipRef.current = w;
      setPipWindow(w);
    } catch {
      // requestWindow rejects when the gesture was consumed or when an
      // enterprise policy blocks PiP. Without this the click would silently
      // do nothing, so fall through to the popup the same as an unsupported
      // browser.
      openPopup();
    }
  }, []);

  // Tear the window down if the opener unmounts (navigation away, sign-out) —
  // otherwise it lingers with a dead React portal inside it.
  useEffect(() => {
    return () => {
      pipRef.current?.close();
      pipRef.current = null;
    };
  }, []);

  const portal = pipWindow ? createPortal(<TimerPanel compact />, pipWindow.document.body) : null;

  return { open, close, isOpen: !!pipWindow, supported: isPipSupported(), portal };
}
